import { io as createSocket } from "socket.io-client";
import { getAuthInstance } from "@tailormed/common-client/services/auth";

import { oktaConfig } from "../constant/okta";
import { SERVER_URL } from "./api";

// Connection to the FN backend's standalone socket.io v4 server (real-time "something changed" pushes).
// Topic-agnostic: callers subscribe to a { type, id } topic; the server pushes a single `update` event.
// Auth: the bearer token rides in the handshake `auth` payload (not the URL) and is verified server-side.
const SOCKET_URL = SERVER_URL.replace(/\/$/, "");

let socket;
// Desired topic subscriptions, replayed on every (re)connect — a reconnect is a brand-new server-side
// socket with no room membership, so we must re-emit subscribe for each topic each time.
const subscribedTopics = new Map(); // "type:id" -> { type, id }
const topicKey = (type, id) => `${type}:${id}`;

function getToken() {
  return getAuthInstance(oktaConfig.redirectUri).getAccessToken();
}

function connectSocket() {
  if (!socket) {
    const token = getToken();
    // Don't open the socket without a token — handing { token: undefined } to the server is never valid.
    if (!token) return undefined;
    socket = createSocket(SOCKET_URL, {
      path: "/socket.io",
      transports: ["websocket"],
      auth: { token }
    });
    socket.on("connect", () => {
      // Re-join rooms after every (re)connect — the reconnected socket has no prior room membership.
      subscribedTopics.forEach(({ type, id }) => socket.emit("subscribe", { type, id }));
    });
    // Use a fresh token on reconnects (the Okta access token rotates). If it's gone, drop the socket.
    socket.io.on("reconnect_attempt", () => {
      const freshToken = getToken();
      if (!freshToken) {
        socket.disconnect();
        return;
      }
      socket.auth = { token: freshToken };
    });
    // Best-effort: log handshake rejects, never surface them to the user.
    socket.on("connect_error", (error) => {
      console.warn("realtime socket connect_error:", error?.message);
    });
  }
  return socket;
}

function getSocket() {
  return socket;
}

function subscribe(type, id) {
  subscribedTopics.set(topicKey(type, id), { type, id });
  const activeSocket = getSocket();
  // If not yet connected, the `connect` handler will replay the subscription.
  if (activeSocket && activeSocket.connected) activeSocket.emit("subscribe", { type, id });
}

function unsubscribe(type, id) {
  subscribedTopics.delete(topicKey(type, id));
  const activeSocket = getSocket();
  if (activeSocket && activeSocket.connected) activeSocket.emit("unsubscribe", { type, id });
}

export { connectSocket, getSocket, subscribe, unsubscribe };
